import React from 'react';
import { CertQuestionOrdering } from '@unity-learning/shared';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import CodeMirror from '@uiw/react-codemirror';
import { cpp } from '@codemirror/lang-cpp';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import { GripVertical } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ContextCode } from './ContextCode';

// Light theme for CodeMirror
const lightTheme = EditorView.theme({
    '&': { backgroundColor: '#f8f9fa' },
    '.cm-gutters': { backgroundColor: '#f0f0f0', borderRight: '1px solid #ddd' },
    '.cm-content': { caretColor: '#333' },
    '.cm-line': { color: '#333' },
});

interface QuestionOrderingProps {
    question: Omit<CertQuestionOrdering, 'validation'>;
    value: string[];
    onChange: (value: string[]) => void;
    disabled?: boolean;
}

interface OrderItem {
    id: string;
    content_type: 'text' | 'code';
    value: string;
}

interface DragItem {
    id: string;
    index: number;
    type: string;
}

const ItemTypes = {
    ORDER_ITEM: 'orderItem'
};

const OrderableItem: React.FC<{
    item: OrderItem;
    index: number;
    moveItem: (dragIndex: number, hoverIndex: number) => void;
    disabled: boolean;
    isDark: boolean;
}> = ({ item, index, moveItem, disabled, isDark }) => {
    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: ItemTypes.ORDER_ITEM,
        item: { id: item.id, index, type: ItemTypes.ORDER_ITEM },
        canDrag: !disabled,
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    }), [index, disabled]);

    const [{ isOver }, drop] = useDrop(() => ({
        accept: ItemTypes.ORDER_ITEM,
        hover: (draggedItem: DragItem) => {
            if (draggedItem.index !== index) {
                moveItem(draggedItem.index, index);
                draggedItem.index = index;
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver()
        })
    }), [index, moveItem]);

    return (
        <div
            ref={(node) => { preview(drop(node)); }}
            className={`
        flex items-start gap-3 p-3 rounded-lg border-2
        transition-all duration-200
        ${isDragging
                    ? 'opacity-50 border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                    : isOver
                        ? 'border-blue-300 bg-blue-25 dark:bg-blue-950/30'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                }
      `}
        >
            {/* Drag Handle */}
            <div
                ref={drag}
                className={`
          flex-shrink-0 p-1 rounded cursor-grab
          ${disabled ? 'cursor-not-allowed text-gray-300' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}
        `}
            >
                <GripVertical className="w-5 h-5" />
            </div>

            {/* Item Content */}
            <div className="flex-1 min-w-0">
                {item.content_type === 'code' ? (
                    <div className="rounded overflow-hidden">
                        <CodeMirror
                            value={item.value}
                            theme={isDark ? oneDark : lightTheme}
                            extensions={[cpp()]}
                            height="auto"
                            editable={false}
                            basicSetup={{
                                lineNumbers: false,
                                foldGutter: false,
                            }}
                        />
                    </div>
                ) : (
                    <div className="text-gray-800 dark:text-gray-200 text-sm">
                        {item.value}
                    </div>
                )}
            </div>

            {/* Position Indicator */}
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400">
                {index + 1}
            </div>
        </div>
    );
};

const QuestionOrderingInner: React.FC<QuestionOrderingProps> = ({
    question,
    value,
    onChange,
    disabled = false
}) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const { content } = question;
    const { items, context_code } = content;

    // Build ordered array of items based on current value
    const orderedItems: OrderItem[] = value.length > 0
        ? value.map(id => items.find(item => item.id === id)!).filter(Boolean)
        : items; // Default to original order if no value yet

    const moveItem = (fromIndex: number, toIndex: number) => {
        if (disabled) return;

        const newOrder = [...orderedItems];
        const [movedItem] = newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, movedItem);

        onChange(newOrder.map(item => item.id));
    };

    return (
        <div className="space-y-2">
            {context_code && (
                <div className="mb-4">
                    <ContextCode code={context_code} />
                </div>
            )}
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Glissez les éléments pour les remettre dans le bon ordre :
            </div>
            {orderedItems.map((item, index) => (
                <OrderableItem
                    key={item.id}
                    item={item}
                    index={index}
                    moveItem={moveItem}
                    disabled={disabled}
                    isDark={isDark}
                />
            ))}
        </div>
    );
};

export const QuestionOrdering: React.FC<QuestionOrderingProps> = (props) => {
    return (
        <DndProvider backend={HTML5Backend}>
            <QuestionOrderingInner {...props} />
        </DndProvider>
    );
};

export default QuestionOrdering;
